/**
 * INBOX — every application that arrived by email, and what was actually done with it.
 *
 *   GET     /orgs/{orgId}/inbound-emails?includeDeleted=
 *   DELETE  /orgs/{orgId}/inbound-emails/{id}
 *   POST    /orgs/{orgId}/inbound-emails/{id}/restore
 *   POST    /requisitions/{reqId}/resumes/{scanId}/match
 *
 * WHY THIS IS ITS OWN PAGE. The intake log used to be a table at the bottom of Connections,
 * capped at 25 rows, with no way to remove anything. That is the wrong home twice over:
 * Connections is about configuring transports (a super-admin task, visited once), while the
 * mailbox is a recruiter's daily working list. Testing the email connector — forwarding a
 * message at a job address to see whether it lands — would leave clutter nobody could clear.
 *
 * THE COLUMN THAT MATTERS IS "Action taken". A recruiter's question is never "what stage is this
 * row at", it is "was this filed, and where — or is it sitting here doing nothing?". The API
 * derives that into one `action` value (see inboundEmail.ts) so the answer is computed once, in
 * the place that holds the facts, rather than re-guessed by every screen that shows it.
 *
 * DISMISSING IS NOT DELETING THE CANDIDATE. A message that produced an applicant produced a real
 * person on a real job, and clearing a log entry must not quietly remove them. The confirm
 * dialog says so in as many words and links to the candidate for anyone who meant that too.
 * Dismissal is soft and reversible — hence the "Dismissed" tab and its Restore.
 */
import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { INBOUND_EMAIL_DOMAIN } from "@talenthop/shared";
import { api, apiFetch, ApiError } from "../../lib/api.js";
import { useAuth } from "../../lib/AuthContext.js";
import { Modal } from "../admin/adminUi.js";
import { Mail, Alert, Info, RefreshCw, Trash, Search, Link2, FileText } from "../../components/icons.js";

// ── wire types ───────────────────────────────────────────────────────────────

/**
 * Mirrors the `action` union derived by GET /orgs/{orgId}/inbound-emails.
 *
 * `unassigned` reads "Job deleted" rather than "no job matched": a message that matched no job
 * at all has no org (migration 0083 refuses to attribute it to a tenant), so it never appears on
 * an org's inbox. A row HERE with no requisition is one whose role was deleted after the fact.
 */
type InboxAction = "matched" | "filed" | "needs_review" | "unassigned" | "none" | "dismissed";

interface InboxEmail {
  id: string;
  requisitionId: string | null;
  requisitionTitle: string | null;
  requisitionState: string | null;
  candidacyId: string | null;
  candidateName: string | null;
  jobCode: string | null;
  toAddress: string;
  fromAddress: string;
  fromName: string | null;
  subject: string | null;
  preview: string | null;
  receivedAt: string;
  stage: string;
  status: string;
  error: string | null;
  hasResume: boolean;
  attachmentCount: number;
  scanId: string | null;
  scanScored: boolean;
  dismissedAt: string | null;
  action: InboxAction;
}

interface InboxCounts {
  total: number;
  filed: number;
  needs_review: number;
  unassigned: number;
  dismissed: number;
}

interface InboxResponse {
  counts: InboxCounts;
  emails: InboxEmail[];
}

// ── presentation ─────────────────────────────────────────────────────────────

/**
 * How each outcome reads and looks. Only a filed or matched application is green: a message
 * sitting in the inbox having done nothing is not a success, and a pill that says otherwise
 * would be the screen lying about the state of someone's application.
 */
const ACTION_LABEL: Record<InboxAction, string> = {
  matched: "Matched & scored",
  filed: "Added to job",
  needs_review: "Needs review",
  unassigned: "Job deleted",
  none: "No action taken",
  dismissed: "Dismissed",
};

const ACTION_PILL: Record<InboxAction, string> = {
  matched: "pill v",
  filed: "pill v",
  needs_review: "pill r",
  unassigned: "pill r",
  none: "pill n",
  dismissed: "pill n",
};

type TabKey = "all" | "filed" | "needs_review" | "unassigned" | "dismissed";

const TABS: ReadonlyArray<{ key: TabKey; label: string; count: keyof InboxCounts }> = [
  { key: "all", label: "All", count: "total" },
  { key: "filed", label: "Added to a job", count: "filed" },
  { key: "needs_review", label: "Needs review", count: "needs_review" },
  { key: "unassigned", label: "Job deleted", count: "unassigned" },
  { key: "dismissed", label: "Dismissed", count: "dismissed" },
];

/**
 * SORTING IS CLIENT-SIDE, and honestly so. The list endpoint already caps at 200 rows and
 * returns them newest-first; sorting that page in the browser is instant and needs no round
 * trip. What it must NOT do is imply it sorted the whole mailbox — so when there are more
 * messages than the page holds, the table says so underneath.
 */
type SortKey = "from" | "job" | "action" | "resume" | "received";

const SORTABLE: ReadonlyArray<{ key: SortKey; label: string }> = [
  { key: "from", label: "From" },
  { key: "job", label: "Job" },
  { key: "action", label: "Action taken" },
  { key: "resume", label: "Resume" },
  { key: "received", label: "Received" },
];

/** Worst-first when sorting by outcome: the rows that need a human come to the top. */
const ACTION_RANK: Record<InboxAction, number> = {
  needs_review: 0,
  unassigned: 1,
  none: 2,
  filed: 3,
  matched: 4,
  dismissed: 5,
};

const sortValue = (e: InboxEmail, key: SortKey): string | number => {
  switch (key) {
    case "from":
      return (e.fromName || e.fromAddress).toLowerCase();
    case "job":
      return (e.requisitionTitle ?? e.jobCode ?? "").toLowerCase();
    case "action":
      return ACTION_RANK[e.action];
    case "resume":
      // Attachment count, so "3 attached" sorts above "1 attached" above "none".
      return e.hasResume ? Math.max(e.attachmentCount, 1) : 0;
    case "received":
      return new Date(e.receivedAt).getTime();
  }
};

// ── detail wire types (GET …/inbound-emails/{id}/detail) ─────────────────────

interface DetailAttachment {
  filename: string;
  mime: string;
  bytes: number;
  isResume: boolean;
  /** Short-lived SAS link, or null when the bytes were never stored. */
  url: string | null;
  storedBytes: boolean;
}

interface EmailDetail {
  email: {
    id: string;
    requisitionId: string | null;
    requisitionTitle: string | null;
    jobCode: string | null;
    toAddress: string;
    ccAddress: string | null;
    fromAddress: string;
    fromName: string | null;
    subject: string | null;
    bodyText: string | null;
    receivedAt: string;
    stage: string;
    status: string;
    error: string | null;
    hasResume: boolean;
    dismissedAt: string | null;
    attachments: DetailAttachment[];
  };
  candidate: {
    id: string;
    name: string | null;
    email: string | null;
    state: string | null;
    pipelineStatus: string | null;
  } | null;
  resume: {
    scanId: string;
    filename: string | null;
    url: string | null;
    storedBytes: boolean;
    /** Null is "filed, not scored" — the normal emailed state. NEVER render it as a zero. */
    overall: number | null;
    summary: string | null;
    components: unknown;
    hasText: boolean;
    textPreview: string | null;
  } | null;
}

/** The rubric payload's shape, to the extent this page reads it. */
interface Rubricish {
  missing?: unknown;
  strengths?: unknown;
}
const listOf = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];

const kb = (bytes: number): string =>
  bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;

const when = (iso: string | null): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  if (mins < 60 * 24) return `${Math.round(mins / 60)}h ago`;
  // Older than a day: date AND time (product review 18 — "dates only, no time").
  return `${d.toLocaleDateString(undefined, { day: "numeric", month: "short" })}, ${d.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
};

/** Resume-score band for the detail ring — lime strong, orange partial, red weak (audit X3). */
const band = (v: number): string => (v >= 70 ? "hi" : v >= 50 ? "mid" : "low");

/**
 * A sortable column header. The arrow is only drawn on the ACTIVE column — an arrow on every
 * header tells you nothing about which one is in effect, and aria-sort carries the same fact to
 * a screen reader rather than leaving it to the glyph.
 */
function SortHeader({
  col,
  sort,
  onSort,
}: {
  col: SortKey;
  sort: { key: SortKey; dir: "asc" | "desc" };
  onSort: (k: SortKey) => void;
}): ReactNode {
  const active = sort.key === col;
  const label = SORTABLE.find((c) => c.key === col)!.label;
  return (
    <th aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}>
      <button
        type="button"
        onClick={() => onSort(col)}
        title={`Sort by ${label.toLowerCase()}`}
        style={{
          background: "none",
          border: "none",
          padding: 0,
          font: "inherit",
          color: "inherit",
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          opacity: active ? 1 : 0.85,
        }}
      >
        {label}
        <span aria-hidden="true" style={{ fontSize: 12, opacity: active ? 0.9 : 0.55 }}>
          {active ? (sort.dir === "asc" ? "▲" : "▼") : "▾"}
        </span>
      </button>
    </th>
  );
}

// ── page ─────────────────────────────────────────────────────────────────────

export function Inbox(): ReactNode {
  const { me } = useAuth();
  const orgId = me?.orgId ?? "";
  const queryClient = useQueryClient();

  const [tab, setTab] = useState<TabKey>("all");
  const [search, setSearch] = useState("");
  const [confirmDismiss, setConfirmDismiss] = useState<InboxEmail | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [sort, setSort] = useState<{ key: SortKey; dir: "asc" | "desc" }>({
    key: "received",
    dir: "desc",
  });
  /** The row whose detail drawer is open. Null when the table is just a table. */
  const [openId, setOpenId] = useState<string | null>(null);

  // Clicking a header toggles direction if it is already the sort, otherwise switches to it.
  // A new column starts DESC for time and score-ish columns and ASC for names, because that is
  // what each one means by "most interesting first".
  const sortBy = (key: SortKey): void =>
    setSort((cur) =>
      cur.key === key
        ? { key, dir: cur.dir === "asc" ? "desc" : "asc" }
        : { key, dir: key === "from" || key === "job" ? "asc" : "desc" },
    );

  // The dismissed tab is the only one that needs the soft-deleted rows, so it is the only one
  // that asks for them. Separate query keys keep the two lists from evicting each other.
  const includeDeleted = tab === "dismissed";

  const { data, isLoading, isError } = useQuery({
    queryKey: ["inbox", orgId, includeDeleted],
    queryFn: () =>
      api.get<InboxResponse>(
        `/orgs/${orgId}/inbound-emails?limit=200${includeDeleted ? "&includeDeleted=1" : ""}`,
      ),
    enabled: orgId.length > 0,
    // Mail keeps arriving while this page sits open, and a row that has been acted on elsewhere
    // should not still be clickable here. Re-read on every mount rather than serving whatever
    // the cache happened to hold — the same thing the Requisitions list does, for the same reason.
    refetchOnMount: "always",
  });

  const counts = data?.counts ?? {
    total: 0,
    filed: 0,
    needs_review: 0,
    unassigned: 0,
    dismissed: 0,
  };

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ["inbox", orgId] });
    // The Connections page shows a slice of the same log, and the candidate hub counts the
    // applicants this produced — both go stale the moment something here changes.
    void queryClient.invalidateQueries({ queryKey: ["inbound-emails", orgId] });
  };

  const emails = useMemo(() => {
    const all = data?.emails ?? [];
    const byTab = all.filter((e) => {
      if (tab === "dismissed") return e.action === "dismissed";
      if (e.action === "dismissed") return false;
      if (tab === "all") return true;
      if (tab === "filed") return e.action === "filed" || e.action === "matched";
      if (tab === "needs_review") return e.action === "needs_review";
      return e.action === "unassigned";
    });
    const q = search.trim().toLowerCase();
    const found = q
      ? byTab.filter((e) =>
          [e.fromName, e.fromAddress, e.subject, e.requisitionTitle, e.candidateName, e.jobCode]
            .filter(Boolean)
            .some((v) => String(v).toLowerCase().includes(q)),
        )
      : byTab;

    const dir = sort.dir === "asc" ? 1 : -1;
    return [...found].sort((a, b) => {
      const av = sortValue(a, sort.key);
      const bv = sortValue(b, sort.key);
      if (av === bv) {
        // Stable, meaningful tie-break: newest first, whatever the primary column is.
        return new Date(b.receivedAt).getTime() - new Date(a.receivedAt).getTime();
      }
      const cmp =
        typeof av === "string" && typeof bv === "string"
          ? av.localeCompare(bv)
          : Number(av) < Number(bv)
            ? -1
            : 1;
      return cmp * dir;
    });
  }, [data, tab, search, sort]);

  // The open row's detail. Fetched only when a row is open, cached per message, so opening the
  // same one twice costs nothing and the 200-row list never carries bodies or signed links.
  const {
    data: detail,
    isLoading: detailLoading,
    error: detailError,
  } = useQuery({
    queryKey: ["inbox-detail", orgId, openId],
    queryFn: () => api.get<EmailDetail>(`/orgs/${orgId}/inbound-emails/${openId}/detail`),
    enabled: orgId.length > 0 && openId !== null,
    // A 404 means the row is gone, not that the network blipped. Retrying it four times just
    // makes the user wait longer to be told the same thing.
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 2,
  });
  const openRow = useMemo(
    () => (openId ? (data?.emails ?? []).find((e) => e.id === openId) ?? null : null),
    [data, openId],
  );

  // Drawer-local UI state: the candidate edit form and the collapsed message body. Both start
  // closed for every message that is opened.
  const [editing, setEditing] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [emailDraft, setEmailDraft] = useState("");
  const [showBody, setShowBody] = useState(false);
  useEffect(() => {
    setEditing(false);
    setShowBody(false);
  }, [openId]);

  // Emailed candidates arrive with whatever the mail carried (a forwarder's name, no address at
  // all), so the recruiter can correct the name and email here — same endpoint the Candidates
  // hub uses, so it updates the candidate everywhere.
  const saveCandidate = useMutation({
    mutationFn: () =>
      api.patch(`/requisitions/${detail?.email.requisitionId}/candidates/${detail?.candidate?.id}`, {
        candidateName: nameDraft,
        candidateEmail: emailDraft,
      }),
    onSuccess: () => {
      setEditing(false);
      setNote("Candidate updated.");
      void queryClient.invalidateQueries({ queryKey: ["inbox-detail", orgId, openId] });
      refresh();
    },
    onError: (err) =>
      setNote(err instanceof ApiError && err.status === 400 ? err.message : "Couldn't save those changes."),
  });

  const dismiss = useMutation({
    mutationFn: (e: InboxEmail) =>
      apiFetch<{ candidacyId: string | null }>(`/orgs/${orgId}/inbound-emails/${e.id}`, {
        method: "DELETE",
      }),
    onSuccess: (_res, e) => {
      setConfirmDismiss(null);
      setNote(
        e.candidacyId
          ? "Message dismissed. The candidate it created is still on their job — remove them from Candidates if you meant that too."
          : "Message dismissed. You can bring it back from the Dismissed tab.",
      );
      refresh();
    },
    onError: () => setNote("Couldn't dismiss that message. Please retry in a moment."),
  });

  const restore = useMutation({
    mutationFn: (e: InboxEmail) => api.post(`/orgs/${orgId}/inbound-emails/${e.id}/restore`, {}),
    onSuccess: () => {
      setNote("Message restored to your inbox.");
      refresh();
    },
    onError: () => setNote("Couldn't restore that message."),
  });

  // OPTIONAL MATCHING, same contract as Connections: an emailed resume is filed unscored on
  // purpose, because scoring spends credits and an arriving email is not a decision to spend
  // them. This is where the recruiter chooses to.
  const runMatch = useMutation({
    mutationFn: (e: InboxEmail) =>
      api.post<{ charged: number; degraded: boolean }>(
        `/requisitions/${e.requisitionId}/resumes/${e.scanId}/match`,
        {},
      ),
    onSuccess: (res) => {
      setNote(
        res.degraded
          ? "Couldn't score that resume just now — you haven't been charged. Try again shortly."
          : "Matched. The score is on the candidate in this job.",
      );
      refresh();
    },
    onError: () => setNote("Couldn't run matching on that resume."),
  });

  return (
    <section className="t1-inbox">
      <div className="page-h">
        <div style={{ minWidth: 0 }}>
          <h1>Inbox</h1>
          <div className="sub">
            Applications forwarded to your job addresses on {INBOUND_EMAIL_DOMAIN}, and what was
            done with each one
          </div>
        </div>
        <button
          className="btn t1-ib-icon"
          onClick={refresh}
          disabled={isLoading}
          aria-label="Refresh the inbox"
          data-tip="Refresh"
          data-tip-pos="left"
        >
          <RefreshCw />
        </button>
      </div>

      {note && (
        <div className="banner info" style={{ marginBottom: 14 }}>
          <Info />
          <div>
            {note}
            <button className="btn sm ghost" style={{ marginLeft: 10 }} onClick={() => setNote(null)}>
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* ── filters ────────────────────────────────────────────────────────── */}
      <div className="t1-ib-toolbar">
        <div className="seg t1-ib-tabs" role="tablist" aria-label="Inbox filter">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              className={`${tab === t.key ? "on" : ""}${counts[t.count] === 0 ? " zero" : ""}`}
              onClick={() => setTab(t.key)}
            >
              {t.label}
              <i className="tnum">{counts[t.count]}</i>
            </button>
          ))}
        </div>
        <label className="t1-ib-search">
          <Search aria-hidden="true" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search sender, subject or job"
            aria-label="Search the inbox"
          />
        </label>
      </div>

      {isLoading && <div className="card pad mut sm">Loading your inbox…</div>}
      {isError && (
        <div className="banner crit">
          <Alert />
          <div>Couldn't load your inbox. Please retry in a moment.</div>
        </div>
      )}

      {/* An empty inbox is the normal state for an org that hasn't switched email receiving on,
          so the empty state points at the setup rather than implying something went wrong. */}
      {!isLoading && !isError && emails.length === 0 && (
        <div className="card pad" style={{ textAlign: "center", padding: 40 }}>
          <Mail style={{ width: 28, height: 28, opacity: 0.5 }} />
          <div style={{ fontWeight: 650, marginTop: 10 }}>
            {counts.total === 0 && tab === "all" ? "No email applications yet" : "Nothing here"}
          </div>
          <div className="mut sm" style={{ maxWidth: 480, margin: "6px auto 0" }}>
            {counts.total === 0 && tab === "all" ? (
              <>
                Every job gets its own address on {INBOUND_EMAIL_DOMAIN}. Forward an application
                there and it shows up here, with what happened to it.
              </>
            ) : (
              <>No messages match this filter.</>
            )}
          </div>
          {counts.total === 0 && tab === "all" && (
            <Link to="/app/connections" className="btn ghost" style={{ marginTop: 16 }}>
              <Link2 /> Set up email receiving
            </Link>
          )}
        </div>
      )}

      {/* ── the list ───────────────────────────────────────────────────────── */}
      {emails.length > 0 && (
        <div className="tbl-wrap card t1-ib-card">
          {/* Every row is at most two lines: the job address sits under the job it routed to (it
              was its own column), and long text is cut to one line with the full text on hover. */}
          <table className="tbl inbox-tbl">
            <colgroup>
              <col className="ib-c-from" />
              <col className="ib-c-job" />
              <col className="ib-c-action" />
              <col className="ib-c-resume" />
              <col className="ib-c-received" />
              <col className="ib-c-actions" />
            </colgroup>
            <thead>
              <tr>
                <SortHeader col="from" sort={sort} onSort={sortBy} />
                <SortHeader col="job" sort={sort} onSort={sortBy} />
                <SortHeader col="action" sort={sort} onSort={sortBy} />
                <SortHeader col="resume" sort={sort} onSort={sortBy} />
                <SortHeader col="received" sort={sort} onSort={sortBy} />
                <th className="t1-ib-th-act">
                  <span className="t1-ib-sr">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {emails.map((e) => (
                // `clickable` is the house class for a row that opens something: it carries the
                // pointer cursor AND the hover tint, which together say "this whole row is the
                // target". Setting cursor inline (as this did) got the cursor and lost the tint,
                // so the row gave no hover feedback at all.
                <tr
                  key={e.id}
                  className="clickable"
                  onClick={() => setOpenId(e.id)}
                  style={e.dismissedAt ? { opacity: 0.6 } : undefined}
                >
                  <td>
                    <div className="ib-line" style={{ fontWeight: 600 }} title={e.fromAddress}>
                      {e.fromName || e.fromAddress}
                    </div>
                    <div className="ib-line mut sm" title={[e.subject, e.preview].filter(Boolean).join(" — ")}>
                      {e.subject || "(no subject)"}
                      {e.preview ? ` — ${e.preview}` : ""}
                    </div>
                  </td>
                  <td data-label="Job" className="mut sm">
                    <div className="ib-line">
                    {e.requisitionId ? (
                      // The title names the DESTINATION, not the element. "Open this job"
                      // beside a row whose own click opens the message is the one sentence
                      // that tells you the two clicks differ, before you make either.
                      <Link
                        to={`/app/requisitions/${e.requisitionId}`}
                        title={`Open the job "${e.requisitionTitle ?? "this job"}"`}
                        onClick={(ev) => ev.stopPropagation()}
                      >
                        {e.requisitionTitle ?? "this job"}
                      </Link>
                    ) : e.jobCode ? (
                      `code ${e.jobCode} — no match`
                    ) : (
                      "—"
                    )}
                    </div>
                    {/* The ORIGINAL recipient, verbatim — the evidence of why the application
                        landed on this job, and what a recruiter asking "why here?" reads. */}
                    <div className="ib-line ib-address" title={e.toAddress}>
                      {e.toAddress}
                    </div>
                  </td>
                  <td data-label="Action">
                    <span className={ACTION_PILL[e.action]}>{ACTION_LABEL[e.action]}</span>

                    {e.action === "needs_review" && e.error && (
                      <div className="ib-line mut sm" style={{ marginTop: 4 }} title={e.error}>
                        {e.error}
                      </div>
                    )}
                    {/* In an ORG-scoped inbox this can only mean one thing. A message that
                        matched no job at all is never attributed to a tenant (0083) and so is
                        not on this list — so a row here with no requisition is one whose role
                        was deleted afterwards, leaving the message behind (the FK is ON DELETE
                        SET NULL). "No job matched" would have been the wrong sentence. */}
                    {e.action === "unassigned" && (
                      <div className="ib-line mut sm" style={{ marginTop: 4 }}>
                        The job this arrived for
                        {e.jobCode ? ` (code ${e.jobCode})` : ""} has been deleted.
                      </div>
                    )}
                  </td>
                  <td data-label="Resume" className="mut sm">
                    {/* Success stays quiet (audit 18): plain text with an icon, colour kept for
                        the exceptions in the Action column. */}
                    {e.hasResume ? (
                      <span className="t1-ib-res">
                        <FileText aria-hidden="true" />
                        Attached{e.attachmentCount > 1 ? ` (${e.attachmentCount})` : ""}
                      </span>
                    ) : (
                      <span className="t1-ib-res none">None</span>
                    )}
                  </td>
                  <td data-label="Received" className="mut sm">
                    {when(e.receivedAt)}
                    {e.dismissedAt && (
                      <div className="mut sm">dismissed {when(e.dismissedAt)}</div>
                    )}
                  </td>
                  <td className="t1-ib-act">
                    <div className="t1-ib-act-in">
                    {e.dismissedAt ? (
                      <button
                        className="btn sm"
                        onClick={(ev) => {
                          ev.stopPropagation();
                          restore.mutate(e);
                        }}
                        disabled={restore.isPending}
                      >
                        Restore
                      </button>
                    ) : (
                      <>
                        {/* Offered only where there is something to score and no score yet —
                            matching runs once per resume, and a re-run would be a second
                            charge for an answer we already have. */}
                        {e.scanId && !e.scanScored && e.requisitionId && (
                          <button
                            className="btn sm"
                            onClick={(ev) => {
                              ev.stopPropagation();
                              runMatch.mutate(e);
                            }}
                            disabled={runMatch.isPending}
                          >
                            Run match
                          </button>
                        )}
                        {/* Neutral at rest, red on hover, and always confirmed (audit X10). */}
                        <button
                          className="t1-ib-del"
                          aria-label="Dismiss this message"
                          data-tip="Dismiss"
                          data-tip-pos="left"
                          onClick={(ev) => {
                            ev.stopPropagation();
                            setConfirmDismiss(e);
                          }}
                        >
                          <Trash style={{ width: 15, height: 15 }} />
                        </button>
                      </>
                    )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {/* The page is capped at 200. Sorting reorders THIS page, so when there are more
              messages than fit, say so rather than letting "oldest first" look complete. */}
          {counts.total > 200 && (
            <div className="mut sm" style={{ padding: "8px 12px" }}>
              Showing the {emails.length} most recent of {counts.total} — sorting and search apply
              to these.
            </div>
          )}
        </div>
      )}

      {/* ── one message, opened ────────────────────────────────────────────── */}
      {openId && (
        <Modal
          title={openRow?.subject || "(no subject)"}
          maxWidth={760}
          onClose={() => setOpenId(null)}
          footer={
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", alignItems: "center" }}>
              {detail?.candidate && (
                <Link
                  to={`/app/candidates?reqId=${detail.email.requisitionId ?? ""}`}
                  className="btn ghost"
                  style={{ marginRight: "auto" }}
                >
                  Open candidate
                </Link>
              )}
              {openRow && !openRow.dismissedAt && openRow.scanId && !openRow.scanScored && openRow.requisitionId && (
                <button
                  className="btn ghost"
                  onClick={() => runMatch.mutate(openRow)}
                  disabled={runMatch.isPending}
                >
                  {runMatch.isPending ? "Matching…" : "Run match"}
                </button>
              )}
              <button className="btn primary" onClick={() => setOpenId(null)}>
                Close
              </button>
            </div>
          }
        >
          {detailLoading && <div className="mut sm">Loading the message…</div>}

          {/* WITHOUT THIS the drawer rendered an empty white box on any failure — a title, a
              Close button and nothing else, with the reason only in the browser console. A 404
              here specifically means the list is showing a row that no longer exists, so say
              that and offer the one action that fixes it. */}
          {!detailLoading && detailError && (
            <div className="banner crit">
              <Alert />
              <div>
                {detailError instanceof ApiError && detailError.status === 404 ? (
                  <>
                    This message is no longer in your inbox — it was removed since this list was
                    loaded.{" "}
                    <button
                      className="btn sm ghost"
                      onClick={() => {
                        setOpenId(null);
                        refresh();
                      }}
                    >
                      Refresh the list
                    </button>
                  </>
                ) : (
                  <>Couldn't load this message. Please close and try again in a moment.</>
                )}
              </div>
            </div>
          )}

          {detail && (
            <div className="t1-ib-detail">
              {/* ── who, where, when ──────────────────────────────────────── */}
              <div className="t1-ib-who">
                <div style={{ fontWeight: 700, fontSize: 16, wordBreak: "break-word" }}>
                  {detail.email.fromName || detail.email.fromAddress}
                </div>
                <div className="mut sm" style={{ wordBreak: "break-all" }}>{detail.email.fromAddress}</div>
                <div className="mut sm" style={{ marginTop: 6 }}>
                  to{" "}
                  <span style={{ fontFamily: "var(--mono, monospace)" }}>
                    {detail.email.toAddress}
                  </span>
                  {detail.email.ccAddress && (
                    <>
                      {" · cc "}
                      <span style={{ fontFamily: "var(--mono, monospace)" }}>
                        {detail.email.ccAddress}
                      </span>
                    </>
                  )}
                </div>
                <div className="mut sm">
                  {new Date(detail.email.receivedAt).toLocaleString()}
                  {detail.email.requisitionTitle && (
                    <>
                      {" · "}
                      <Link to={`/app/requisitions/${detail.email.requisitionId}`}>
                        {detail.email.requisitionTitle}
                      </Link>
                    </>
                  )}
                </div>
              </div>

              {detail.email.error && (
                <div className="banner crit">
                  <Alert />
                  <div>{detail.email.error}</div>
                </div>
              )}

              {/* ── STATUS and SCORE, side by side ─────────────────────────
                  Two columns rather than a stack: they are the two questions a recruiter
                  opens this for ("who is this, and where are they?" / "is it any good?"),
                  they are each short, and stacking them pushed the attachments and the
                  message below the fold. `auto-fit` collapses to one column under ~620px
                  so the pair still reads on a narrow window. */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                  gap: 14,
                  alignItems: "start",
                }}
              >
                {/* ── where this person actually stands ─────────────────── */}
                <div className="card pad t1-ib-panel" style={{ height: "100%" }}>
                  <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
                    <div className="t1-ib-h">Status</div>
                    {detail.candidate && detail.email.requisitionId && !editing && (
                      <button
                        className="btn sm ghost"
                        style={{ marginLeft: "auto" }}
                        onClick={() => {
                          setNameDraft(detail.candidate?.name ?? "");
                          setEmailDraft(detail.candidate?.email ?? "");
                          setEditing(true);
                        }}
                      >
                        Edit
                      </button>
                    )}
                  </div>
                  {detail.candidate && editing ? (
                    <div style={{ display: "grid", gap: 8 }}>
                      <label className="mut sm">
                        Name
                        <input
                          className="input"
                          value={nameDraft}
                          onChange={(e) => setNameDraft(e.target.value)}
                          maxLength={160}
                        />
                      </label>
                      <label className="mut sm">
                        Email
                        <input
                          className="input"
                          type="email"
                          value={emailDraft}
                          placeholder="candidate@example.com"
                          onChange={(e) => setEmailDraft(e.target.value)}
                        />
                      </label>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button
                          className="btn sm"
                          disabled={saveCandidate.isPending}
                          onClick={() => saveCandidate.mutate()}
                        >
                          {saveCandidate.isPending ? "Saving…" : "Save"}
                        </button>
                        <button className="btn sm ghost" onClick={() => setEditing(false)}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : detail.candidate ? (
                    <div className="mut sm" style={{ lineHeight: 1.7 }}>
                      <div style={{ wordBreak: "break-word" }}>
                        <b>{detail.candidate.name || "Candidate"}</b>
                      </div>
                      {detail.candidate.email ? (
                        <div style={{ wordBreak: "break-all" }}>{detail.candidate.email}</div>
                      ) : (
                        <div>No email found — use Edit to add one.</div>
                      )}
                      <div style={{ marginTop: 6 }}>
                        Stage <span className="pill pv">{detail.candidate.state ?? "—"}</span>
                        {detail.candidate.pipelineStatus && (
                          <>
                            {" "}
                            <span className="pill t">{detail.candidate.pipelineStatus}</span>
                          </>
                        )}
                      </div>
                    </div>
                  ) : (
                    // No candidate is a real outcome, not a loading state — say which one.
                    <div className="mut sm">
                      No candidate was created from this message.
                      {detail.email.error ? " See the reason above." : ""}
                    </div>
                  )}
                </div>

                {/* ── score ──────────────────────────────────────────────── */}
                <div className="card pad t1-ib-panel" style={{ height: "100%" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                    <div className="t1-ib-h">Resume score</div>
                    {/* A null overall is "filed, not scored" — NEVER rendered as a zero, which
                        would read as "we assessed this person and they scored nothing". */}
                    {!detail.resume ? (
                      <span className="pill n" style={{ marginLeft: "auto" }}>
                        no CV
                      </span>
                    ) : detail.resume.overall === null ? (
                      <span className="pill n" style={{ marginLeft: "auto" }}>
                        not scored
                      </span>
                    ) : (
                      <span
                        className={`t1c-ring ${band(detail.resume.overall)}`}
                        style={{ marginLeft: "auto", ["--v" as string]: detail.resume.overall } as React.CSSProperties}
                        role="img"
                        aria-label={`Resume score ${detail.resume.overall}%`}
                      >
                        <span>{detail.resume.overall}%</span>
                      </span>
                    )}
                  </div>

                  {!detail.resume ? (
                    <div className="mut sm" style={{ lineHeight: 1.6 }}>
                      No CV arrived with this message, so there is nothing to score yet.
                    </div>
                  ) : detail.resume.overall === null ? (
                    <div className="mut sm" style={{ lineHeight: 1.6 }}>
                      Emailed CVs are filed unscored — scoring uses a resume from your plan, and an
                      arriving email isn't a decision to use one. Use <b>Run match</b> below.
                    </div>
                  ) : (
                    <>
                      {detail.resume.summary && (
                        <div className="mut sm" style={{ lineHeight: 1.6 }}>
                          {detail.resume.summary}
                        </div>
                      )}
                      {listOf((detail.resume.components as Rubricish | null)?.missing).length >
                        0 && (
                        <div style={{ marginTop: 10 }}>
                          <div className="mut sm" style={{ fontWeight: 600 }}>
                            Gaps against this role
                          </div>
                          <ul
                            className="mut sm"
                            style={{ margin: "4px 0 0 18px", lineHeight: 1.6 }}
                          >
                            {listOf((detail.resume.components as Rubricish | null)?.missing)
                              .slice(0, 5)
                              .map((m, i) => (
                                <li key={i}>{m}</li>
                              ))}
                          </ul>
                        </div>
                      )}
                      {/* The engine ranks, a human decides (GROUND_RULES §4) — so the score is
                          information, with the full picture one click away. */}
                      {detail.candidate && (
                        <div className="mut sm" style={{ marginTop: 10 }}>
                          <Link to={`/app/review/${detail.candidate.id}`}>
                            See the full assessment
                          </Link>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* ── ATTACHMENTS, full width under the pair ─────────────────── */}
              <div className="card pad t1-ib-panel">
                <div className="t1-ib-h" style={{ marginBottom: 8 }}>
                  Attachments <span className="mut sm">({detail.email.attachments.length})</span>
                </div>
                {detail.email.attachments.length === 0 && (
                  <div className="mut sm">Nothing was attached to this message.</div>
                )}
                {detail.email.attachments.map((a, i) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "8px 0",
                      borderTop: i === 0 ? "none" : "1px solid var(--line)",
                    }}
                  >
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div
                        style={{
                          fontWeight: 600,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={a.filename}
                      >
                        {a.filename}
                      </div>
                      <div className="mut sm">
                        {a.mime} · {kb(a.bytes)}
                        {a.isResume && " · treated as the CV"}
                      </div>
                    </div>
                    <div style={{ flex: "none", whiteSpace: "nowrap" }}>
                      {a.url ? (
                        // A short-lived signed link, opened in a new tab. rel is mandatory:
                        // target=_blank without it hands the opener to the new tab.
                        <a
                          className="btn sm ghost"
                          href={a.url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Open
                        </a>
                      ) : (
                        // Never a dead button — say WHY there is nothing to open.
                        <span className="mut sm" title="The file itself was not kept">
                          not stored
                        </span>
                      )}
                    </div>
                  </div>
                ))}
                {detail.email.attachments.some((a) => !a.storedBytes) && (
                  <div className="mut sm" style={{ marginTop: 8 }}>
                    A file shown as <b>not stored</b> arrived while file storage was unavailable.
                    The text was still read out of it, so the score is unaffected — only the
                    original document is gone.
                  </div>
                )}
              </div>

              {/* ── the message itself, as text under everything ───────────── */}
              <div>
                <div style={{ display: "flex", alignItems: "center", marginBottom: 6 }}>
                  <div className="t1-ib-h">Message</div>
                  <button
                    className="btn sm ghost"
                    style={{ marginLeft: "auto" }}
                    onClick={() => setShowBody((v) => !v)}
                  >
                    {showBody ? "Hide message" : "Show message ›"}
                  </button>
                </div>
                {showBody && (
                  <div
                    className="t1-ib-body"
                  >
                    {detail.email.bodyText || "(no body)"}
                  </div>
                )}
              </div>

              {/* ── what we read out of the CV ─────────────────────────────── */}
              {detail.resume?.hasText && detail.resume.textPreview && (
                <details>
                  <summary style={{ cursor: "pointer", fontWeight: 650 }}>
                    Extracted CV text
                  </summary>
                  <div
                    className="mut sm"
                    style={{
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      lineHeight: 1.55,
                      marginTop: 8,
                    }}
                  >
                    {detail.resume.textPreview}
                  </div>
                </details>
              )}
            </div>
          )}
        </Modal>
      )}

      {/* ── dismiss ────────────────────────────────────────────────────────── */}
      {confirmDismiss && (
        <Modal
          title="Dismiss this message?"
          onClose={() => setConfirmDismiss(null)}
          footer={
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button className="btn ghost" onClick={() => setConfirmDismiss(null)}>
                Keep it
              </button>
              <button
                className="btn primary"
                onClick={() => dismiss.mutate(confirmDismiss)}
                disabled={dismiss.isPending}
              >
                {dismiss.isPending ? "Dismissing…" : "Dismiss"}
              </button>
            </div>
          }
        >
          <div className="mut sm" style={{ lineHeight: 1.6 }}>
            It comes off your inbox list and can be restored from the Dismissed tab — nothing is
            permanently erased.
            {/* Stated up front, not discovered afterwards: the one consequence people assume
                happens here is the one that deliberately does not. */}
            {confirmDismiss.candidacyId ? (
              <div style={{ marginTop: 10 }}>
                <b>
                  {confirmDismiss.candidateName || "The candidate"} stays on{" "}
                  {confirmDismiss.requisitionTitle || "their job"}.
                </b>{" "}
                Dismissing the email doesn't remove the applicant it created, or their resume and
                any score. Remove them from{" "}
                <Link to={`/app/candidates?reqId=${confirmDismiss.requisitionId ?? ""}`}>
                  Candidates
                </Link>{" "}
                if that's what you meant.
              </div>
            ) : (
              <div style={{ marginTop: 10 }}>
                This message never produced a candidate, so nothing else is affected.
              </div>
            )}
            <div style={{ marginTop: 10 }}>
              If the same message is delivered again, we'll still recognise it as one we've
              already seen rather than filing it twice.
            </div>
          </div>
        </Modal>
      )}
    </section>
  );
}
